'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import contenu from '@/content/contenu';
import { CHAMP_PIEGE, CODES, schemaContact, type DemandeDeContact } from '@/content/schemas';
import Bouton from '@/components/ui/Bouton';
import Champ from '@/components/ui/Champ';
import { cn } from '@/lib/cn';
import { envoiReussi } from '@/lib/suiviDAudience';

/**
 * Le formulaire de contact : quatre champs, un envoi, un message de confirmation.
 *
 * ⚠️ C'EST UN COMPOSANT CLIENT, ET LA DEROGATION QUI L'AUTORISE EST BORNEE PAR DOSSIER.
 * `spec-design.md` v7 § La derogation aux composants client ouvre nommement `/contact` ;
 * `tests/support/gardes/client.js` enumere `components/contact`, et `AC-ADM-020` refuse la directive
 * ailleurs. La page d'accueil n'importe rien d'ici.
 *
 * POURQUOI IL EST CLIENT ALORS QUE LA FICHE `07-08` DEMANDE UN RENDU AU SERVEUR « SI RIEN N'IMPOSE
 * LE CONTRAIRE ». Deux choses l'imposent, et elles se verifient :
 *
 *   1. `CONVENTIONS.md` § Un seul schema de validation, partage : le navigateur doit refuser ce que
 *      la route refuse, avec le MEME schema. Un formulaire rendu au serveur ne fait tourner aucun
 *      schema avant l'envoi, et les messages de `contenu.interface` ne s'afficheraient jamais.
 *   2. `AC-CTC-003` mesure la limite de debit PAR ADRESSE IP sur une route. Un envoi de formulaire
 *      classique aurait demande une action de serveur, qui n'expose pas l'adresse de la meme facon
 *      et ne se poste pas directement dans un controle.
 *
 * CE QUE CE CHOIX COUTE, ET IL FAUT L'ECRIRE : la page `/contact` telecharge le runtime de React et
 * `react-hook-form`, comme `/inscription`. La page d'accueil, elle, n'en telecharge rien, et
 * `AC-ADM-021` le mesure.
 *
 * IL NE PORTE AUCUN TEXTE. Les libelles, l'aide, le bouton et les deux messages viennent de
 * `contenu.contact`, recopies de `contenu-site.md` v4 § Le formulaire de contact ; les messages
 * d'erreur de saisie viennent de `contenu.interface`, § Les libelles d'interface. Les codes du
 * schema ne s'affichent jamais : ce composant les traduit, et c'est sa seule raison de connaitre
 * `CODES`.
 */
const formulaire = contenu.contact;
const libelles = contenu.interface;

/** Les quatre champs du tableau de la spec, dans son ordre, et le nom que chacun porte a l'envoi. */
const CHAMPS = [
  { nom: 'prenomNom', type: 'text' },
  { nom: 'email', type: 'email' },
  { nom: 'telephone', type: 'tel' },
] as const;

type NomDeChamp = keyof DemandeDeContact;

/** Le code rendu par le schema, traduit dans le libelle que la spec ecrit. */
function messagePour(code: string): string {
  if (code === CODES.email) return libelles.emailMalForme;
  return libelles.champObligatoireVide;
}

export default function FormulaireDeContact() {
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);

  const {
    register,
    getValues,
    setError,
    setFocus,
    clearErrors,
    formState: { errors },
  } = useForm<DemandeDeContact>({ mode: 'onSubmit' });

  async function envoie(evenement: React.FormEvent<HTMLFormElement>) {
    evenement.preventDefault();
    if (envoi) return;

    const lecture = schemaContact.safeParse({
      ...getValues(),
      [CHAMP_PIEGE]: getValues(CHAMP_PIEGE) ?? '',
    });

    if (!lecture.success) {
      const enErreur: NomDeChamp[] = [];
      for (const probleme of lecture.error.issues) {
        const champ = probleme.path[0] as NomDeChamp | undefined;
        if (!champ) continue;
        setError(champ, { message: messagePour(probleme.message) });
        enErreur.push(champ);
      }
      // LE FOCUS PART SUR LE PREMIER CHAMP EN ERREUR. Sans lui, un lecteur au clavier apprend qu il
      // y a une erreur et doit redescendre le formulaire a l aveugle pour la trouver.
      if (enErreur.length > 0) setFocus(enErreur[0]);
      return;
    }

    clearErrors();
    setEchec(null);
    setEnvoi(true);
    try {
      const reponse = await fetch(`/api/contact${window.location.search}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(lecture.data),
      });

      if (reponse.ok) {
        // LA LIGNE EST ECRITE : le message de confirmation peut donc dire « C est envoye » sans que
        // ce soit une promesse. Il remplace le formulaire, et ne s ajoute pas sous lui.
        //
        // LE TROISIEME EVENEMENT DU SUIVI D'AUDIENCE PART ICI, apres la reponse du serveur, et il
        // porte le nom du formulaire en propriete. RM-ANX-003, AC-ANX-013.
        envoiReussi('contact');
        setEnvoye(true);
        return;
      }

      setEchec(formulaire.messages.echec);
    } catch {
      // UNE COUPURE RESEAU SE RACONTE COMME UN ECHEC TECHNIQUE : c est le meme message, et il porte
      // l adresse de repli.
      setEchec(formulaire.messages.echec);
    } finally {
      setEnvoi(false);
    }
  }

  if (envoye) {
    return (
      <div
        data-ecran="contact-envoye"
        role="status"
        className="flex flex-col gap-6 rounded-carte border border-bordure bg-fond-eleve p-6"
      >
        <p className="font-corps text-corps text-texte-courant">
          {formulaire.messages.confirmation}
        </p>
        <p className="font-corps text-legende">
          <Link
            href="/"
            data-lien="retour-journee"
            className="text-texte-accent underline underline-offset-[3px] hover:text-action-survol"
          >
            {formulaire.lien}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={envoie} className="flex flex-col gap-6">
      {CHAMPS.map((champ, rang) => (
        <Champ
          key={champ.nom}
          identifiant={champ.nom}
          libelle={formulaire.champs[rang].libelle}
          type={champ.type}
          obligatoire={formulaire.champs[rang].obligatoire}
          erreur={errors[champ.nom]?.message}
          enregistrement={register(champ.nom)}
        />
      ))}

      <div className="flex flex-col gap-2">
        <label
          htmlFor="message"
          className="font-corps font-medium text-corps text-texte-courant"
        >
          {formulaire.champs[3].libelle}
        </label>
        <p id="message-aide" className="font-corps text-legende text-texte-doux">
          {formulaire.aideDuMessage}
        </p>
        <textarea
          id="message"
          rows={6}
          aria-describedby={errors.message ? 'message-erreur' : 'message-aide'}
          aria-invalid={errors.message ? true : undefined}
          {...register('message')}
          className={cn(
            'w-full rounded-champ border bg-fond-eleve px-4 py-3',
            'font-corps text-corps text-texte-courant',
            'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
            errors.message ? 'border-action-survol' : 'border-bordure focus:border-action',
          )}
        />
        {errors.message ? (
          <p id="message-erreur" className="font-corps text-legende text-action-survol">
            {errors.message.message}
          </p>
        ) : null}
      </div>

      {/*
        LE CHAMP PIEGE. Il porte `hidden`, `tabIndex` a -1 et `aria-hidden` : un lecteur au clavier ne
        le trouve pas, et il n'existe ni pour l'oeil, ni pour un lecteur d'ecran. Seul un robot qui
        remplit tout ce qu'il trouve le remplit, et la route rejette alors sans ecrire.
      */}
      <input type="text" hidden tabIndex={-1} aria-hidden="true" autoComplete="off" {...register(CHAMP_PIEGE)} />

      <div
        data-contact-erreurs
        aria-live="assertive"
        role="alert"
        className="font-corps text-legende text-action-survol"
      >
        {echec}
      </div>

      <div className="flex flex-col gap-4">
        <Bouton
          repere="contact-envoyer"
          libelle={envoi ? libelles.boutonEnvoiEnCours : formulaire.bouton}
          pleineLargeur
        />
        <p className="font-corps text-legende">
          <Link
            href="/"
            data-lien="retour-journee"
            className="text-texte-accent underline underline-offset-[3px] hover:text-action-survol"
          >
            {formulaire.lien}
          </Link>
        </p>
      </div>
    </form>
  );
}
